import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface PovertyByRaceBarProps {
  location: string;
}

// Bar chart: poverty rate by race for latest year
const PovertyByRaceBar: React.FC<PovertyByRaceBarProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);
  const [latestYear, setLatestYear] = React.useState<number | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const geoid = String(geofips);

    // Fetch latest year available
    const { data: pvrYears } = await supabaseClient
      .from('poverty_by_race')
      .select('year')
      .eq('geoid', geoid)
      .order('year', { ascending: false })
      .limit(1);

    const year = pvrYears && pvrYears.length > 0 ? Number(pvrYears[0].year) : null;

    if (!year) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setLatestYear(year);

    const { data: pvr } = await supabaseClient
      .from('poverty_by_race')
      .select('race, percentage')
      .eq('geoid', geoid)
      .eq('year', year)
      .order('percentage', { ascending: false });

    if (!pvr || pvr.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setHasData(true);

    const races = pvr.map((r: any) => r.race || 'Unknown');
    const percentages = pvr.map((r: any) => Number(r.percentage || 0));

    // Generate colors for bars - using red tones to indicate poverty
    const colors = races.map((_, idx) => {
      const hue = 0 + (idx * 10); // Varying red/orange tones
      const saturation = 60 + (idx % 3) * 10;
      return `hsla(${hue}, ${saturation}%, 50%, 0.7)`;
    });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: races,
        datasets: [
          {
            label: 'Poverty Rate',
            data: percentages,
            backgroundColor: colors,
            borderColor: colors.map(c => c.replace('0.7', '1')),
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx: any) => `${(ctx.parsed.y || 0).toFixed(1)}%`,
            }
          }
        },
        scales: {
          y: {
            title: { display: true, text: 'Poverty Rate (%)' },
            beginAtZero: true,
            ticks: {
              callback: function(value: any) {
                return value + '%';
              }
            }
          },
          x: {
            title: { display: true, text: 'Race/Ethnicity' },
            ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 },
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });

    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasRef.current) {
          const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No poverty by race data available for {location}.</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} style={{ height: '400px' }} />
    </div>
  );
};

export default PovertyByRaceBar;

